<!-- eslint-disable vue/no-unused-vars -->
<template>
  <b-container fluid>
    <!-- Loading spinner tant que l'utilisateur n'est pas initialisé -->
    <div
      v-if="userInitializing"
      class="d-flex justify-content-center align-items-center"
      style="min-height: 50vh"
    >
      <div class="text-center">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem"
        ></b-spinner>
        <div class="mt-3">Loading...</div>
      </div>
    </div>

    <!-- Contenu principal une fois l'utilisateur initialisé -->
    <div v-else>
      <h1>Content</h1>

      <hr />

      <!-- Filtres généraux -->
      <GeneralFilters
        :filters="generalFilters"
        :can-change-language="canChangeLanguage"
        @apply="applyGeneralFilters"
        @reset="resetGeneralFilters"
      />

      <!-- Intégration du composant QuestionsSection -->
      <QuestionsSection
        :themes-list="themesList"
        :types-list="typesList"
        :levels-list="levelsListNames"
        :general-filters="generalFilters"
        @questions-updated="onQuestionsUpdated"
      />

      <!-- Games collapse button -->
      <GamesSection
        :themes="themesList"
        :levels="levelsList"
        :general-filters="generalFilters"
        @games-updated="onGamesUpdated"
      />

      <hr class="mt-5" />

      <ContentDiffStats
        :title="`Unpublished edits`"
        :created-questions="currentVersionDiffStats.questions.created"
        :updated-questions="currentVersionDiffStats.questions.updated"
        :deleted-questions="currentVersionDiffStats.questions.deleted"
        :created-games="currentVersionDiffStats.games.created"
        :updated-games="currentVersionDiffStats.games.updated"
        :deleted-games="currentVersionDiffStats.games.deleted"
        @show-details="
          openContentDiffStatsModal($event.type, $event.status, {
            latestVersionOnly: true,
          })
        "
      />

      <b-button
        variant="success"
        size="lg"
        block
        :disabled="publishBetaLoading"
        @click="handlePublishBeta"
      >
        <b-spinner small v-if="publishBetaLoading"></b-spinner>
        <span>{{
          publishBetaLoading ? 'Publishing...' : 'Publish a BETA'
        }}</span>
      </b-button>

      <hr class="mb-5" />

      <!-- Versions section -->
      <h2 class="mb-3">Publish a new stable version</h2>

      <!-- Latest stable version -->
      <div class="mb-5">
        <h4>Latest stable version</h4>
        <!-- Skeleton loading -->
        <b-card v-if="latestStableVersionLoading">
          <b-skeleton-wrapper>
            <b-skeleton width="60%"></b-skeleton>
            <b-skeleton width="40%"></b-skeleton>
          </b-skeleton-wrapper>
        </b-card>
        <!-- Content -->
        <b-card v-else-if="latestStableVersion">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>Version {{ latestStableVersion.version }}</strong>
              <div class="text-muted">
                {{ formatDate(latestStableVersion.createdAt) }}
              </div>
            </div>
            <b-badge variant="success">Stable</b-badge>
          </div>
        </b-card>
        <!-- No stable version -->
        <b-card v-else class="text-center text-muted">
          No stable version available
        </b-card>
      </div>

      <!-- Beta versions -->
      <h4>Beta versions</h4>
      <VersionsTable
        :list="betaVersions"
        :fields-only="[
          'selected',
          'version',
          'numberVersion',
          'description',
          'language',
          'createdAt',
          'actions',
        ]"
        :show-diff-btn="true"
        :general-filters="generalFilters"
        @selected="onBetaVersionSelect"
        @deleted="onBetaVersionDelete"
      />

      <ContentDiffStats
        title="Edits that will be published"
        :created-questions="versionsDiff.questions.created"
        :updated-questions="versionsDiff.questions.updated"
        :deleted-questions="versionsDiff.questions.deleted"
        :created-games="versionsDiff.games.created"
        :updated-games="versionsDiff.games.updated"
        :deleted-games="versionsDiff.games.deleted"
        @show-details="
          openContentDiffStatsModal($event.type, $event.status, {
            versions: selectedBetaNumbersVersions,
          })
        "
      />

      <div class="mt-3 d-flex justify-content-between">
        <b-button
          variant="primary"
          size="lg"
          block
          class="ml-2"
          :disabled="selectedBetaVersions.length === 0"
          @click="showPublishModal = true"
        >
          Publish a STABLE
        </b-button>
      </div>

      <hr />

      <b-modal
        v-model="showPublishModal"
        title="Publication confirmation"
        ok-title="Publish"
        cancel-title="Cancel"
        :ok-disabled="publishLoading"
        @ok="handlePublishStable"
        footer-class="d-flex flex-row-reverse justify-content-start"
      >
        <p>
          Are you sure you want to publish a new stable version with the
          following beta versions:
        </p>
        <ul>
          <li v-for="version in selectedBetaVersions" :key="version._id">
            Version {{ version.version }} - {{ version.description }}
          </li>
        </ul>
        <template #modal-footer="{ ok, cancel }">
          <b-button variant="primary" @click="ok()" :disabled="publishLoading">
            <b-spinner small v-if="publishLoading"></b-spinner>
            <span>{{ publishLoading ? 'Publishing...' : 'Publish' }}</span>
          </b-button>
          <b-button
            variant="secondary"
            @click="cancel()"
            :disabled="publishLoading"
          >
            Cancel
          </b-button>
        </template>
      </b-modal>

      <b-modal
        v-model="showContentDiffStatsModal"
        :title="contentDiffStatsModalTitle"
        :hide-footer="true"
        @hidden="closeContentDiffModal"
      >
        <div v-if="contentDiffLoading" class="flex-center">
          <b-spinner variant="primary" class="m-5"></b-spinner>
        </div>
        <div v-else>
          <!-- Questions -->
          <div v-if="contentDiffType === 'questions'">
            <div
              v-for="question in paginatedItems"
              :key="'q__' + question.id"
              class="content-diff-item"
            >
              <QuestionContentResume
                :key="'q-resume__' + question.id"
                :title="`${contentDiffElementTitle} #${question.id}`"
                :variant="contentDiffElementVariant"
                :title-icon="contentDiffElementTitleIcon"
                :question="question"
              />
            </div>
          </div>

          <!-- Games -->
          <div v-if="contentDiffType === 'games'">
            <div
              v-for="game in paginatedItems"
              :key="'g__' + game.id"
              class="content-diff-item"
            >
              <GameContentResume
                :key="'g-resume__' + game._id"
                :title="`${contentDiffElementTitle}`"
                :variant="contentDiffElementVariant"
                :title-icon="contentDiffElementTitleIcon"
                :game="game"
              />
            </div>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-center mt-4" v-if="totalPages > 1">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              align="center"
              size="md"
              class="my-0"
            ></b-pagination>
          </div>
        </div>
      </b-modal>
    </div>
  </b-container>
</template>

<script>
  /*eslint-disable no-unused-vars*/
  import questionService from '@/services/questionService'
  import gameService from '@/services/gameService'
  import moment from 'moment'
  import VersionsTable from '@/components/VersionsTable.vue'
  import ContentDiffStats from '../components/ContentDiffStats.vue'
  import QuestionsSection from '@/components/content/QuestionsSection.vue'
  import GamesSection from '@/components/content/GamesSection.vue'
  import QuestionContentResume from '@/components/question/QuestionContentResume.vue'
  import GameContentResume from '@/components/game/GameContentResume.vue'
  import GeneralFilters from '@/components/content/GeneralFilters.vue'
  import versionService from '@/services/versionService'
  import { useAuthStore } from '@/stores/auth'

  // Configurer moment en français
  moment.locale('fr')

  export default {
    name: 'ContentManagerPage',
    components: {
      VersionsTable,
      ContentDiffStats,
      QuestionsSection,
      GamesSection,
      QuestionContentResume,
      GameContentResume,
      GeneralFilters,
    },
    props: [],
    async created() {
      // S'assurer que le store d'auth est initialisé
      await this.ensureAuthInitialized()

      // Initialiser les filtres depuis l'URL en premier
      this.initializeFiltersFromUrl()

      // Marquer l'utilisateur comme initialisé
      this.userInitializing = false
    },
    mounted() {
      // Les données seront chargées via le watcher userInitializing
    },
    computed: {
      authStore() {
        return useAuthStore()
      },
      userLanguage() {
        return this.authStore.user?.language || 'fr'
      },
      jwt() {
        return localStorage.getItem('jwt')
      },
      selectedBetaNumbersVersions() {
        return this.selectedBetaVersions.map((v) => v.numberVersion)
      },
      paginatedItems() {
        if (
          !this.currentVersionDiff ||
          !this.contentDiffType ||
          !this.contentDiffStatus
        ) {
          return []
        }
        const items =
          this.currentVersionDiff[this.contentDiffType][this.contentDiffStatus]
        const start = (this.currentPage - 1) * this.perPage
        const end = start + this.perPage
        return items.slice(start, end)
      },
      totalItems() {
        if (
          !this.currentVersionDiff ||
          !this.contentDiffType ||
          !this.contentDiffStatus
        ) {
          return 0
        }
        return this.currentVersionDiff[this.contentDiffType][
          this.contentDiffStatus
        ].length
      },
      totalPages() {
        return Math.ceil(this.totalItems / this.perPage)
      },
      levelsListNames() {
        return this.levelsList.map((level) => level.value)
      },
      currentLanguageFilter() {
        const language = this.generalFilters?.language
        return language && language !== '' ? language : 'fr'
      },
      canChangeLanguage() {
        return !this.authStore.hasRole('content-manager')
      },
    },
    watch: {
      userInitializing(newVal) {
        // Quand l'utilisateur n'est plus en cours d'initialisation, charger les données
        if (newVal === false) {
          this.fetchThemes()
          this.fetchTypes()
          this.fetchLevels()
          this.refreshData()
        }
      },
    },
    data() {
      return {
        userInitializing: true, // État de chargement de l'utilisateur
        questions: [],
        actions: [],
        importGameMediasCollapsed: false,
        gamesCollapsed: false,
        themesList: [],
        typesList: [],
        levelsList: [],
        latestStableVersion: null,
        latestStableVersionLoading: false,
        betaVersions: [],
        betaVersionsLoading: false,
        selectedBetaVersions: [],
        showPublishModal: false,
        publishLoading: false,
        publishBetaLoading: false,

        // Pagination
        currentPage: 1,
        perPage: 10,

        // Différences de contenu
        showContentDiffStatsModal: false,
        contentDiffStatsModalTitle: null,

        // Variable pour l'appel à l'API de différences de contenu
        contentDiffLoading: false,
        contentDiffType: null,
        contentDiffStatus: null,

        // Variables pour l'affichage des différences de contenu
        contentDiffElementTitle: '',
        contentDiffElementVariant: 'primary',
        contentDiffElementTitleIcon: 'plus-circle-fill',
        currentVersionDiffStats: {
          questions: {
            created: 0,
            updated: 0,
            deleted: 0,
          },
          games: {
            created: 0,
            updated: 0,
            deleted: 0,
          },
        },
        currentVersionDiff: {},
        versionsDiff: {
          questions: {
            created: 0,
            updated: 0,
            deleted: 0,
          },
          games: {
            created: 0,
            updated: 0,
            deleted: 0,
          },
        },
        // Filtres généraux
        generalFilters: {
          versions: [],
          language: 'fr', // Sera mis à jour dans mounted()
        },
      }
    },
    methods: {
      async fetchThemes() {
        const themes = await questionService.getThemes()
        this.themesList = themes
      },

      onQuestionsUpdated(questionsData) {
        if (questionsData) {
          this.fetchDiffStats()
        }
      },

      async fetchLatestStableVersion() {
        try {
          const analytics = await versionService.getAnalytics({
            language: this.currentLanguageFilter,
          })

          // On cherche la dernière version stable dans les analytics
          return analytics.data && analytics.data.latest
            ? analytics.data.latest
            : null
        } catch (error) {
          console.error('Error fetching latest stable version:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to fetch the latest stable version',
          })
          return null
        }
      },

      async fetchUnusedBetaVersions() {
        try {
          return await versionService.getUnusedBetaVersions({
            language: this.currentLanguageFilter,
          })
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to fetch beta versions',
          })
          return []
        }
      },

      formatDate(dateString) {
        return moment(dateString).format('D MMMM YYYY [à] HH:mm')
      },

      onBetaVersionSelect(items) {
        this.selectedBetaVersions = items

        // Appel à l'API pour récupérer les différences entre les versions
        this.fetchDiffStatsOfVersions()
      },

      onBetaVersionDelete(version) {
        // Refresh
        this.fetchUnusedBetaVersions()
          .then((betaVersions) => {
            this.betaVersions = betaVersions
          })
          .finally(() => {
            this.betaVersionsLoading = false
          })

        // Suppression de la version dans les versions sélectionnées
        this.selectedBetaVersions = this.selectedBetaVersions.filter(
          (v) => v._id !== version._id
        )

        // Appel à l'API pour récupérer les différences entre les versions
        this.fetchDiffStatsOfVersions()
        this.fetchDiffStats()
      },

      async fetchDiffStatsOfVersions() {
        if (this.selectedBetaVersions.length === 0) {
          this.versionsDiff = {
            questions: {
              created: 0,
              updated: 0,
              deleted: 0,
            },
            games: {
              created: 0,
              updated: 0,
              deleted: 0,
            },
          }
          return
        }

        try {
          const diffStats = await versionService.getDiffStats({
            language: this.currentLanguageFilter,
            versions: this.selectedBetaNumbersVersions,
          })
          this.versionsDiff = diffStats
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to fetch difference statistics',
          })
        }
      },

      async refreshData() {
        // Chargement de la dernière version stable
        this.latestStableVersionLoading = true
        this.fetchLatestStableVersion()
          .then((stableVersion) => {
            this.latestStableVersion = stableVersion
          })
          .finally(() => {
            this.latestStableVersionLoading = false
          })

        // Chargement des versions beta
        this.betaVersionsLoading = true
        this.fetchUnusedBetaVersions()
          .then((betaVersions) => {
            this.betaVersions = betaVersions
          })
          .finally(() => {
            this.betaVersionsLoading = false
          })

        // Chargement du résumé des différences entre les données confirmées et les données temporaires
        this.fetchDiffStats()
      },


      notifyMissingZipMedias(versionPublished) {
        const missingMedias = versionPublished?.missingMedias || []
        if (missingMedias.length === 0) {
          return
        }

        const labels = missingMedias
          .map((media) => media.name || media.destinationPath || media.sourcePath)
          .join(', ')

        this.$notify({
          type: 'warn',
          title: 'Incomplete zip',
          text: `${missingMedias.length} media file(s) could not be added to the zip: ${labels}. You may delete this version if needed.`,
        })
      },

      async handlePublishStable(bvModalEvent) {
        bvModalEvent.preventDefault()
        this.publishLoading = true

        try {
          const versionPublished =
            await versionService.publishDataVersionStable({
              versions: this.selectedBetaVersions.map((v) => v._id || v.id),
              language: this.currentLanguageFilter,
            })

          this.$notify({
            type: 'success',
            title: 'Stable version published',
            text: `Version ${versionPublished?.version || ''} has been published successfully`,
          })
          this.notifyMissingZipMedias(versionPublished)

          this.showPublishModal = false
          await this.refreshData()
          this.selectedBetaVersions = []
        } catch (error) {
          console.error('Error during stable publication:', error)
          const publishError = error.response?.data?.error
          this.$notify({
            type: publishError?.code === 'PUBLISH_IN_PROGRESS' ? 'warn' : 'error',
            title:
              publishError?.code === 'PUBLISH_IN_PROGRESS'
                ? 'Publication already in progress'
                : 'Error',
            text:
              publishError?.message ||
              error.response?.data?.error ||
              'An error occurred during publication',
          })
        } finally {
          this.publishLoading = false
        }
      },

      async handlePublishBeta() {
        this.publishBetaLoading = true
        try {
          const versionPublished = await versionService.publishDataVersionBeta({
            language: this.currentLanguageFilter,
          })

          this.$notify({
            type: 'success',
            title: 'Beta version published',
            text: `Version ${versionPublished?.version || ''} has been published successfully`,
          })
          this.notifyMissingZipMedias(versionPublished)

          // Rafraîchir les données
          await this.refreshData()
        } catch (error) {
          console.error('Error during beta publication:', error)
          const publishError = error.response?.data?.error
          this.$notify({
            type: publishError?.code === 'PUBLISH_IN_PROGRESS' ? 'warn' : 'error',
            title:
              publishError?.code === 'PUBLISH_IN_PROGRESS'
                ? 'Publication already in progress'
                : 'Error',
            text:
              publishError?.message ||
              error.response?.data?.error ||
              'An error occurred during publication',
          })
        } finally {
          this.publishBetaLoading = false
        }
      },

      /**
       * Récupère la liste des types de questions
       */
      async fetchTypes() {
        const types = await questionService.getTypes()
        this.typesList = types
      },

      /**
       * Récupère la liste des niveaux
       */
      async fetchLevels() {
        try {
          const response = await gameService.getLevels()
          this.levelsList = response.items
        } catch (error) {
          console.error(error)
        }
      },

      /**
       * Récupère le résumé des modifications entre les données confirmées et les données temporaires
       */
      async fetchDiffStats() {
        this.currentVersionDiffStats = {
          questions: {
            created: 0,
            updated: 0,
            deleted: 0,
          },
          games: {
            created: 0,
            updated: 0,
            deleted: 0,
          },
        }

        try {
          const diffStats = await versionService.getDiffStats({
            language: this.currentLanguageFilter,
            versions: [],
            latestVersionOnly: true,
          })
          this.currentVersionDiffStats = diffStats
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to fetch difference statistics',
          })
        }
      },

      /**
       * Gestion des événements des composants
       */
      onGamesUpdated(gamesData) {
        this.fetchDiffStats()
      },

      /**
       * Ouvre le modal de différences de contenu.
       * On récupère auprès de l'API les différences de contenu en fonction des critères souhaités.
       */
      async openContentDiffStatsModal(type, status, queryParams = {}) {
        this.showContentDiffStatsModal = true
        this.contentDiffStatsModalTitle = `Content differences...`

        try {
          this.contentDiffLoading = true
          this.contentDiffType = type
          this.contentDiffStatus = status

          this.currentVersionDiff = await versionService.getDiff({
            ...queryParams,
            language: this.currentLanguageFilter,
          })
        } catch (error) {
          console.error('Error retrieving content differences:', error)
          this.$notify({
            type: 'error',
            title: 'Error',
            text: 'Unable to retrieve content differences',
          })
        } finally {
          this.contentDiffLoading = false
        }

        if (this.contentDiffType === 'questions') {
          if (this.contentDiffStatus === 'created') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.questions.created.length} Questions added`
            this.contentDiffElementTitle = 'Question added'
            this.contentDiffElementVariant = 'success'
            this.contentDiffElementTitleIcon = 'plus-circle-fill'
          } else if (this.contentDiffStatus === 'updated') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.questions.updated.length} Questions edited`
            this.contentDiffElementTitle = 'Question edited'
            this.contentDiffElementVariant = 'warning'
            this.contentDiffElementTitleIcon = 'pencil-fill'
          } else if (this.contentDiffStatus === 'deleted') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.questions.deleted.length} Questions deleted`
            this.contentDiffElementTitle = 'Question deleted'
            this.contentDiffElementVariant = 'danger'
            this.contentDiffElementTitleIcon = 'x-circle-fill'
          }
        } else if (this.contentDiffType === 'games') {
          if (this.contentDiffStatus === 'created') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.games.created.length} Games added`
            this.contentDiffElementTitle = 'Game added'
            this.contentDiffElementVariant = 'success'
            this.contentDiffElementTitleIcon = 'plus-circle-fill'
          } else if (this.contentDiffStatus === 'updated') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.games.updated.length} Games edited`
            this.contentDiffElementTitle = 'Game edited'
            this.contentDiffElementVariant = 'warning'
            this.contentDiffElementTitleIcon = 'pencil-fill'
          } else if (this.contentDiffStatus === 'deleted') {
            this.contentDiffStatsModalTitle = `${this.currentVersionDiff.games.deleted.length} Games deleted`
            this.contentDiffElementTitle = 'Game deleted'
            this.contentDiffElementVariant = 'danger'
            this.contentDiffElementTitleIcon = 'x-circle-fill'
          }
        }
      },

      /**
       * Ferme le modal de différences de contenu.
       */
      closeContentDiffModal() {
        this.showContentDiffStatsModal = false
        this.contentDiffStatsModalTitle = null
        this.contentDiffType = null
        this.contentDiffStatus = null
        this.currentVersionDiff = {}
        this.currentPage = 1
      },

      /**
       * Initialise les filtres depuis les paramètres d'URL
       */
      initializeFiltersFromUrl() {
        const urlParams = new URLSearchParams(window.location.search)

        // Récupérer les versions depuis l'URL
        const versionsParam = urlParams.get('versions')
        let versions = []
        if (versionsParam) {
          versions = versionsParam
            .split(',')
            .map((v) => parseInt(v))
            .filter((v) => !isNaN(v))
        }

        // Récupérer la langue depuis l'URL ou utiliser celle de l'utilisateur
        const language = urlParams.get('language') || this.userLanguage

        // Mettre à jour les filtres existants au lieu de les remplacer
        this.generalFilters.versions = versions
        this.generalFilters.language = language
      },

      /**
       * Met à jour les paramètres d'URL avec les filtres actuels
       */
      updateUrlFromFilters() {
        if (!this.generalFilters) return

        const urlParams = new URLSearchParams(window.location.search)

        // Mettre à jour les versions
        if (
          this.generalFilters.versions &&
          this.generalFilters.versions.length > 0
        ) {
          urlParams.set('versions', this.generalFilters.versions.join(','))
        } else {
          urlParams.delete('versions')
        }

        // Mettre à jour la langue
        if (this.generalFilters.language) {
          urlParams.set('language', this.generalFilters.language)
        } else {
          urlParams.delete('language')
        }

        // Mettre à jour l'URL sans recharger la page
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname
        window.history.replaceState({}, '', newUrl)
      },

      /**
       * Applique les filtres généraux
       */
      applyGeneralFilters(filters) {
        this.generalFilters = filters
        // Mettre à jour l'URL avec les nouveaux filtres
        this.updateUrlFromFilters()
        // Rafraîchir les données avec les nouveaux filtres
        this.refreshData()
      },

      /**
       * S'assurer que les informations d'authentification sont disponibles
       */
      async ensureAuthInitialized() {
        await this.authStore.waitForAuthReady()
      },

      /**
       * Réinitialise les filtres généraux
       */
      resetGeneralFilters() {
        this.generalFilters = {
          versions: [],
          language: this.userLanguage,
        }
        // Mettre à jour l'URL pour supprimer les filtres
        this.updateUrlFromFilters()
        // Rafraîchir les données sans filtres
        this.refreshData()
      },
    },
  }
</script>

<style scoped>
  .pagination {
    margin-bottom: 0;
  }
</style>
